/**
 * Deposit Verification Panel - SOP #3
 * Dedicated quote verification surface (separate from Quote Management).
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Button,
  Alert,
  Space,
  Tag,
  Divider,
  message,
} from "antd";
import {
  FileProtectOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import DepositVerificationModal from "./DepositVerificationModal";
import DepositReceiptViewModal from "./DepositReceiptViewModal";

const { Text } = Typography;

const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const MetricCard = styled.div`
  padding: 0.875rem 1rem;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
`;

const MetricLabel = styled(Text)`
  display: block;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 0.25rem;
`;

const MetricValue = styled(Text)`
  display: block;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
`;

const resolveRequiredDeposit = (quote, totalAmount) => {
  const fromDepositApi = Number(quote?.deposit?.requiredAmount);
  if (Number.isFinite(fromDepositApi) && fromDepositApi >= 0) {
    return fromDepositApi;
  }
  if (quote?.payment?.requiredAmountOverridden === true) {
    const overridden = Number(quote?.payment?.requiredAmount);
    if (Number.isFinite(overridden) && overridden >= 0) {
      return overridden;
    }
  }
  const fromMinimum = Number(quote?.deposit?.minimumAmount);
  if (Number.isFinite(fromMinimum) && fromMinimum >= 0) {
    return fromMinimum;
  }
  return Number.isFinite(totalAmount) && totalAmount > 0
    ? Math.round(totalAmount * 0.2)
    : 0;
};

const DepositVerificationPanel = ({
  quote,
  onUpdate,
  userRole,
  userId,
  canManage,
  openVerifyNonce = 0,
}) => {
  const navigate = useNavigate();
  const [depositVerificationModalVisible, setDepositVerificationModalVisible] =
    useState(false);
  const [depositReceiptViewModalVisible, setDepositReceiptViewModalVisible] =
    useState(false);

  const canVerifyDeposit =
    (canManage || userRole === "admin") &&
    ["accepted", "pending_payment", "pending", "sent", "approved"].includes(
      quote?.status
    ) &&
    !quote?.payment?.depositVerified;

  const isDepositVerified =
    quote?.payment?.depositVerified || quote?.status === "deposit_verified";

  // Support quick-action from quote header: open verify/receipt modal immediately
  useEffect(() => {
    if (!openVerifyNonce) return;
    if (canVerifyDeposit) {
      setDepositVerificationModalVisible(true);
    } else if (isDepositVerified) {
      setDepositReceiptViewModalVisible(true);
    }
  }, [openVerifyNonce, canVerifyDeposit, isDepositVerified]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount || 0);
  };

  const totalAmount = Number(
    quote?.totalCost !== undefined
      ? quote.totalCost
      : (quote?.estimatedCost || 0) + (quote?.addOnsCost || 0)
  );
  const requiredDeposit = resolveRequiredDeposit(quote, totalAmount);
  const orderId = quote?.payment?.orderId || quote?.orderId;

  return (
    <>
      <Card
        title={
          <Space>
            <FileProtectOutlined />
            <Text>Deposit Verification</Text>
            <Tag color="blue">SOP #3</Tag>
          </Space>
        }
        size="small"
      >
        <PanelBody>
          <Text type="secondary">
            Verify the customer deposit, generate the receipt, and create the
            linked order from this tab.
          </Text>

          <MetricGrid>
            <MetricCard>
              <MetricLabel>Quote Total</MetricLabel>
              <MetricValue style={{ color: "#1890ff" }}>
                {formatCurrency(totalAmount)}
              </MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Deposit Required</MetricLabel>
              <MetricValue style={{ color: "#fa8c16" }}>
                {formatCurrency(requiredDeposit)}
              </MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Verification Status</MetricLabel>
              {isDepositVerified ? (
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  style={{ marginTop: 4 }}
                >
                  Deposit Verified
                </Tag>
              ) : (
                <Tag color="default" style={{ marginTop: 4 }}>
                  Not Verified
                </Tag>
              )}
            </MetricCard>
          </MetricGrid>

          {canVerifyDeposit && (
            <>
              <Divider style={{ margin: "4px 0" }} />
              <Alert
                type="info"
                showIcon
                message="Ready to verify"
                description="Confirm the bank deposit against the expected amount, then issue the receipt and order."
              />
              <Button
                type="primary"
                size="large"
                icon={<FileProtectOutlined />}
                onClick={() => setDepositVerificationModalVisible(true)}
                block
                data-testid="verify-deposit-button"
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
              >
                Verify Deposit (SOP #3)
              </Button>
            </>
          )}

          {isDepositVerified && (
            <>
              <Alert
                message="Deposit Verified"
                description={
                  <Space direction="vertical" size={4}>
                    <Text>
                      Deposit has been verified.
                      {orderId ? (
                        <>
                          {" "}
                          Order ID:{" "}
                          <Button
                            type="link"
                            size="small"
                            icon={<ShoppingCartOutlined />}
                            onClick={() => navigate(`/orders/${orderId}`)}
                            style={{ padding: 0, height: "auto" }}
                          >
                            {orderId}
                          </Button>
                        </>
                      ) : (
                        " Order ID: N/A"
                      )}
                    </Text>
                    {quote?.payment?.depositVerifiedAt && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Verified{" "}
                        {new Date(
                          quote.payment.depositVerifiedAt
                        ).toLocaleString()}
                        {quote.payment.depositVerifiedBy
                          ? ` by ${quote.payment.depositVerifiedBy}`
                          : ""}
                      </Text>
                    )}
                  </Space>
                }
                type="success"
                showIcon
              />
              <Button
                type="primary"
                icon={<FileProtectOutlined />}
                onClick={() => setDepositReceiptViewModalVisible(true)}
                block
                data-testid="view-receipt-button"
              >
                View Receipt & Templates
              </Button>
            </>
          )}

          {!canVerifyDeposit && !isDepositVerified && (
            <Alert
              type="warning"
              showIcon
              icon={<DollarOutlined />}
              message="Verification not available yet"
              description={
                <Text type="secondary">
                  Deposit verification becomes available when this quote is in a
                  payable status and you have quote update permission. Current
                  status: <Text strong>{quote?.status || "unknown"}</Text>
                </Text>
              }
            />
          )}
        </PanelBody>
      </Card>

      <DepositVerificationModal
        visible={depositVerificationModalVisible}
        quote={quote}
        onClose={() => setDepositVerificationModalVisible(false)}
        onSuccess={() => {
          setDepositVerificationModalVisible(false);
          if (onUpdate) onUpdate();
          message.success("Deposit verified successfully!");
        }}
        onQuoteRefresh={() => {
          if (onUpdate) onUpdate();
        }}
        userId={userId}
      />

      <DepositReceiptViewModal
        visible={depositReceiptViewModalVisible}
        quote={quote}
        onClose={() => setDepositReceiptViewModalVisible(false)}
      />
    </>
  );
};

export default DepositVerificationPanel;
